import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';

export function useSubmitWorkflowApproval() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: true },
    Error,
    {
      workflowRunId: string;
      stepRunId: string;
      decision: 'approved' | 'rejected' | 'escalated';
      comment?: string;
    }
  >({
    mutationFn: async ({ stepRunId, decision, comment }) => {
      return await rpcClient.workflows.submitWorkflowApproval({
        stepRunId,
        decision,
        comment,
      });
    },
    onSuccess: (_data, variables) => {
      // Fire-and-forget on purpose: awaiting these would keep the mutation
      // pending until every refetch settles, freezing the decision buttons.
      void queryClient.invalidateQueries({
        queryKey: ['workflow-step-runs', variables.workflowRunId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', variables.workflowRunId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['workflow-runs'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['workflow-approvals'],
      });
      // The backend archives the gate's "Approval requested" notifications on
      // decision — refetch the inbox feed so they drop out immediately.
      void queryClient.invalidateQueries({
        queryKey: ['inbox-notifications'],
      });
    },
  });
}

/**
 * Admin-only: dismiss a pending approval item (e.g. one whose subject was
 * deleted, leaving a dangling inbox card). Nothing is approved or rejected —
 * the gate's raised ask is retired and its notifications archived.
 */
export function useDismissWorkflowApproval() {
  const queryClient = useQueryClient();

  return useMutation<{ success: true }, Error, { stepRunId: string }>({
    mutationFn: async ({ stepRunId }) => {
      return await rpcClient.workflows.dismissWorkflowApproval({ stepRunId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workflow-approvals'] });
      void queryClient.invalidateQueries({
        queryKey: ['inbox-notifications'],
      });
    },
  });
}
